import type { RefObject } from "react";
import { useLayoutEffect, useState } from "react";

/** Room left above a picker for the floating chat header, so it never covers the bot name. */
const HEADER_ROOM = 72;
/** A picker never shrinks below about two rows; past that it scrolls instead of growing. */
const MIN_PICKER_HEIGHT = 96;

/**
 * The tallest a composer picker may be: the space between the top of the chat and the composer
 * bar it sits on. The picker scrolls inside that height, so no ancestor ever has to scroll.
 */
export function useComposerPickerMaxHeight(
  open: boolean,
  barRef: RefObject<HTMLElement | null>,
): number | undefined {
  const [maxHeight, setMaxHeight] = useState<number>();
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const bar = barRef.current;
      if (!bar) return;
      // Measured from the chat's bottom: an overflowing picker moves the bar, never the chat.
      const chat = bar.closest("main")?.getBoundingClientRect();
      const chatTop = Math.max(chat?.top ?? 0, 0);
      const chatBottom = Math.min(chat?.bottom ?? window.innerHeight, window.innerHeight);
      const barRect = bar.getBoundingClientRect();
      const composerBottom = bar.closest("fieldset")?.getBoundingClientRect().bottom;
      const below = (composerBottom ?? barRect.bottom) - barRect.top;
      setMaxHeight(composerPickerMaxHeight(chatBottom - below, chatTop));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, barRef]);
  return maxHeight;
}

export function composerPickerMaxHeight(barTop: number, chatTop: number) {
  // The picker's own bottom margin sits between it and the bar.
  return Math.max(Math.floor(barTop - chatTop - HEADER_ROOM - 8), MIN_PICKER_HEIGHT);
}

/**
 * Scrolls a listbox so its highlighted option is fully visible, moving only the list itself.
 * `scrollIntoView` would also scroll every ancestor, which once pushed the chat header off screen.
 */
export function scrollOptionIntoList(list: HTMLElement, option: HTMLElement) {
  const top = option.offsetTop;
  const bottom = top + option.offsetHeight;
  if (top < list.scrollTop) list.scrollTop = top;
  else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
}
